/**
 * Ручной исторический (вне заказа) ввод уровня риска профрисков по рабочему месту
 * (p2-manual-historical, §11.3, Б7). Роль-агностично: клиент и команда используют один
 * и тот же компонент. Уровень риска ограничен тремя значениями (высокий/средний/
 * допустимый — БЕЗ «не оценено», в отличие от результата заказа). Документ — загрузка
 * доступна только команде (`canUploadDocument`, design D7). Точка входа: карточка сотрудника
 * (§1.2, p3-structure-sout-training design D6, `openLabel`/`closeLabel` — переименование
 * кнопки под текст ТЗ без смены поведения). Со страницы «Профриски» точка входа убрана
 * (centrika-docs, page.occupational-safety.profrisks, MR !23) — сам компонент при этом
 * не менялся.
 */
import { useEffect, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { profriskApi, teamApi, type HistoricalRiskLevel, type ProfriskHistoricalResult } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { RISK_LEVEL_LABELS } from '@/lib/profrisk'
import { formatHistoricalDate } from '@/sout/historical-sout-form'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { DateInput } from '@/design-system/components/primitives/date-input'
import { FileInput } from '@/design-system/components/primitives/file-input'
import { Alert } from '@/design-system/components/feedback/alert'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

/** §11.3 «Допустимые значения уровня риска»: только эти три (без not_assessed). */
const HISTORICAL_RISK_LEVELS: HistoricalRiskLevel[] = ['high', 'medium', 'acceptable']

/** Тип документа «Карты профрисков» — уже засеян (00001_init_schema.sql). */
const PROFRISK_CARDS_TYPE = 'profrisk_cards'

interface ProfriskDraft {
  riskLevel: HistoricalRiskLevel | ''
  assessedOn: string
  comment: string
}

function emptyProfriskDraft(): ProfriskDraft {
  return { riskLevel: '', assessedOn: '', comment: '' }
}

/** Короткое название записи для диалога удаления: «Высокий · 01.06.2024». */
function recordTitle(item: ProfriskHistoricalResult): string {
  return `${RISK_LEVEL_LABELS[item.riskLevel]} · ${formatHistoricalDate(item.assessedOn)}`
}

export interface HistoricalProfriskPanelProps {
  workplaceId: string
  /** Загрузка документа доступна только команде (design D7). */
  canUploadDocument?: boolean
  /**
   * Текст кнопки-переключателя в закрытом состоянии (design D6): карточка сотрудника
   * (p3-structure-sout-training §1.2) требует текст «Добавить исторические профриски» —
   * прочие точки входа (заказ) сохраняют прежний текст по умолчанию.
   */
  openLabel?: string
  /** Текст кнопки в открытом состоянии («Скрыть …», по умолчанию — прежний текст). */
  closeLabel?: string
  /**
   * Записи видны сразу, без раскрытия панели (карточка сотрудника): список исторических записей
   * с «Править»/«Удалить» загружается при открытии карточки, а кнопка `openLabel` открывает форму
   * новой записи. Без признака — прежняя сворачиваемая панель (заказ: по панели на строку
   * таблицы, список грузится по раскрытию).
   */
  inlineRecords?: boolean
  /**
   * Запись сохранена или удалена — колбэк для вызывающей стороны, чтобы перезагрузить соседние блоки,
   * которые сами не подписаны на этот стор (сводка «Информация по профрискам» карточки
   * сотрудника, code-review волны 2). Панель всегда перезагружает СВОЙ список сама (`load()`) —
   * этот колбэк дополнительный, необязательный; без него старые точки входа не меняются.
   */
  onSaved?: () => void
}

export function HistoricalProfriskPanel({
  workplaceId,
  canUploadDocument = false,
  openLabel = 'Историческая оценка',
  closeLabel = 'Скрыть историю профрисков',
  inlineRecords = false,
  onSaved,
}: HistoricalProfriskPanelProps) {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<ProfriskHistoricalResult[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<ProfriskDraft>(emptyProfriskDraft())
  const [file, setFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<ProfriskHistoricalResult | null>(null)
  const [deleting, setDeleting] = useState(false)

  function load() {
    setLoading(true)
    setLoadError(null)
    profriskApi.historical
      .list(workplaceId)
      .then(setItems)
      .catch((err) => setLoadError(getErrorMessage(err, 'Не удалось загрузить историю профрисков')))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (open || inlineRecords) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inlineRecords, workplaceId])

  function startCreate() {
    setEditingId('new')
    setDraft(emptyProfriskDraft())
    setFile(null)
    setSaveError(null)
  }

  function startEdit(item: ProfriskHistoricalResult) {
    setEditingId(item.id)
    setDraft({ riskLevel: item.riskLevel, assessedOn: item.assessedOn ?? '', comment: item.comment ?? '' })
    setFile(null)
    setSaveError(null)
  }

  async function save() {
    if (!draft.riskLevel) return
    setSaveError(null)
    setSaving(true)
    try {
      let documentFileId: string | undefined
      if (file && canUploadDocument) {
        const uploaded = await teamApi.uploadFile({ file, documentTypeCode: PROFRISK_CARDS_TYPE })
        documentFileId = uploaded.id
      }
      const input = {
        workplaceId,
        riskLevel: draft.riskLevel,
        assessedOn: draft.assessedOn,
        comment: draft.comment.trim() || undefined,
        documentFileId,
      }
      if (editingId && editingId !== 'new') {
        await profriskApi.historical.update(editingId, input)
      } else {
        await profriskApi.historical.create(input)
      }
      setEditingId(null)
      load()
      onSaved?.()
    } catch (err) {
      setSaveError(getErrorMessage(err, 'Не удалось сохранить историческую запись'))
    } finally {
      setSaving(false)
    }
  }

  /** Удаление необратимо — выполняется только после подтверждения в диалоге. */
  async function confirmAndRemove() {
    if (!confirmDelete) return
    const id = confirmDelete.id
    setSaveError(null)
    setDeleting(true)
    try {
      await profriskApi.historical.remove(id)
      if (editingId === id) setEditingId(null)
      load()
      onSaved?.()
    } catch (err) {
      setSaveError(getErrorMessage(err, 'Не удалось удалить запись'))
    } finally {
      setDeleting(false)
      setConfirmDelete(null)
    }
  }

  const alerts = (
    <>
      {loadError ? <Alert variant="danger">{loadError}</Alert> : null}
      {saveError ? <Alert variant="danger">{saveError}</Alert> : null}
    </>
  )

  const list = (
    <ul className="flex flex-col gap-2">
      {items.map((item) => (
        <li
          key={item.id}
          className="flex flex-wrap items-center justify-between gap-2 rounded-8 border border-line p-2 text-13"
        >
          <span>
            {RISK_LEVEL_LABELS[item.riskLevel]} · {formatHistoricalDate(item.assessedOn)}
            {item.comment ? ` · ${item.comment}` : ''}
            {item.documentUrl ? (
              <>
                {' · '}
                <a className="text-accent-txt underline" href={item.documentUrl} download>
                  документ
                </a>
              </>
            ) : null}
          </span>
          <span className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => startEdit(item)}>
              Править
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setConfirmDelete(item)}>
              Удалить
            </Button>
          </span>
        </li>
      ))}
    </ul>
  )

  const form = (
    <div className="mt-3 flex flex-col">
      <Field label="Уровень риска" required>
        {(props) => (
          <Select
            value={draft.riskLevel}
            onValueChange={(v) => setDraft((d) => ({ ...d, riskLevel: v as HistoricalRiskLevel }))}
          >
            <SelectTrigger
              id={props.id}
              aria-label="Уровень риска"
              aria-required={props['aria-required']}
            >
              <SelectValue placeholder="Выберите уровень" />
            </SelectTrigger>
            <SelectContent>
              {HISTORICAL_RISK_LEVELS.map((lvl) => (
                <SelectItem key={lvl} value={lvl}>
                  {RISK_LEVEL_LABELS[lvl]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>
      <Field label="Дата оценки" required>
        {(props) => (
          <DateInput
            {...props}
            value={draft.assessedOn}
            onChange={(assessedOn) => setDraft((d) => ({ ...d, assessedOn }))}
          />
        )}
      </Field>
      <Field label="Комментарий">
        {(props) => (
          <Input
            {...props}
            value={draft.comment}
            placeholder="Необязательно"
            onChange={(e) => setDraft((d) => ({ ...d, comment: e.target.value }))}
          />
        )}
      </Field>
      {canUploadDocument ? (
        <Field label="Документ-карта (необязательно)">
          {(props) => (
            <FileInput {...props} value={file} onChange={setFile} disabled={saving} />
          )}
        </Field>
      ) : null}
      <div className="flex gap-2">
        <Button disabled={saving || !draft.riskLevel || !draft.assessedOn} onClick={() => void save()}>
          {saving ? 'Сохранение…' : 'Сохранить'}
        </Button>
        <Button variant="secondary" onClick={() => setEditingId(null)}>
          Отмена
        </Button>
      </div>
    </div>
  )

  const confirmDialog = (
    <Dialog
      open={confirmDelete !== null}
      onOpenChange={(next) => {
        if (!next) setConfirmDelete(null)
      }}
    >
      {confirmDelete ? (
        <DialogContent
          title="Удалить исторические профриски?"
          description={`Запись «${recordTitle(confirmDelete)}» будет удалена.`}
          className="max-w-sm"
        >
          <Card header="Удалить исторические профриски?">
            <p className="text-txt-secondary">
              Запись «{recordTitle(confirmDelete)}» будет удалена, сведения по профрискам
              рабочего места пересчитаются. Действие необратимо.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                size="sm"
                className="border-red bg-red text-white hover:opacity-90"
                disabled={deleting}
                onClick={() => void confirmAndRemove()}
                leftIcon={<Trash2 className="size-4 stroke-current" aria-hidden />}
              >
                Удалить
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setConfirmDelete(null)}>
                Отмена
              </Button>
            </div>
          </Card>
        </DialogContent>
      ) : null}
    </Dialog>
  )

  if (inlineRecords) {
    return (
      <div className="mt-2">
        {alerts}
        {items.length > 0 ? (
          <div className="mb-2">
            <p className="py-1 text-13 text-txt-secondary">Исторические записи (внесены вручную)</p>
            {list}
          </div>
        ) : null}
        {editingId ? (
          form
        ) : (
          <Button size="sm" variant="secondary" onClick={startCreate}>
            {openLabel}
          </Button>
        )}
        {confirmDialog}
      </div>
    )
  }

  return (
    <div className="mt-2">
      <Button size="sm" variant="secondary" onClick={() => setOpen((v) => !v)}>
        {open ? closeLabel : openLabel}
      </Button>
      {open ? (
        <Card header="Историческая оценка профрисков (вне заказа)" className="mt-2">
          {alerts}
          {loading ? (
            <p className="py-2 text-txt-secondary">Загрузка…</p>
          ) : (
            <>
              {items.length === 0 ? (
                <p className="py-2 text-txt-secondary">Исторических записей пока нет.</p>
              ) : (
                list
              )}
              {editingId ? (
                form
              ) : (
                <Button className="mt-3" size="sm" onClick={startCreate}>
                  Добавить историческую запись
                </Button>
              )}
            </>
          )}
        </Card>
      ) : null}
      {confirmDialog}
    </div>
  )
}
